import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "../lib/api";
import { AlertTriangle, RefreshCw, X, CheckCircle2 } from "lucide-react";

/**
 * AdminHealthBanner — polls /api/health every 60s.
 *
 * Surfaces production deploy regressions inline for the admin operator:
 *   - CORS failure   (axios "Network Error" with no response.status)
 *   - DB down        (status_code 503, payload.db = false)
 *   - Auth misfire   (401/403 on a public endpoint = misconfigured route)
 *   - Generic 5xx    (any other server error)
 *
 * Only triggers a banner after 2 consecutive failures to avoid flapping on
 * a single dropped request. The banner is dismissable per session.
 */
const POLL_MS = 60_000;
const FAIL_THRESHOLD = 2;

function classifyError(err) {
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return { kind: "cors", label: "Backend unreachable / CORS blocked",
             hint: "Browser blocked the request — usually a CORS or DNS issue on the deployed backend." };
  }
  const s = err?.response?.status;
  if (s === 401 || s === 403) {
    return { kind: "auth", label: `Auth misconfigured (HTTP ${s})`,
             hint: "/api/health is public — a 401/403 here means a routing or auth-middleware regression." };
  }
  if (s === 503) {
    const dbErr = err?.response?.data?.db_error;
    return { kind: "db", label: "Database unavailable",
             hint: dbErr ? `Driver reported: ${dbErr}` : "The backend can't reach MongoDB right now." };
  }
  if (s && s >= 500) {
    return { kind: "server", label: `Server error (HTTP ${s})`,
             hint: "Check backend logs for an unhandled exception in the deployed pod." };
  }
  return { kind: "unknown", label: "Health check failed",
           hint: err?.message || "Unknown error contacting /api/health." };
}

export default function AdminHealthBanner() {
  const [issue, setIssue] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [checking, setChecking] = useState(false);
  const failCountRef = useRef(0);

  const ping = useCallback(async () => {
    setChecking(true);
    try {
      const r = await api.get("/health", { timeout: 8000 });
      if (r.data?.db === false) {
        // 200 but degraded — backend chose to return ok status; still treat as fail.
        failCountRef.current += 1;
        if (failCountRef.current >= FAIL_THRESHOLD) {
          setIssue({ kind: "db", label: "Database unavailable",
                     hint: r.data?.db_error ? `Driver reported: ${r.data.db_error}` : "DB ping failed." });
        }
      } else {
        failCountRef.current = 0;
        setIssue(null);
      }
    } catch (err) {
      failCountRef.current += 1;
      if (failCountRef.current >= FAIL_THRESHOLD) {
        setIssue(classifyError(err));
      }
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    ping();
    const t = setInterval(ping, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [ping]);

  if (!issue || dismissed) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div data-testid="admin-health-banner"
         className="bg-[#FEF3C7] border-b border-[#F59E0B]/40 px-4 py-3 flex flex-wrap items-start gap-3 text-sm">
      <AlertTriangle size={18} className="text-[#B45309] shrink-0 mt-0.5" strokeWidth={2}/>
      <div className="flex-1 min-w-[200px]">
        <div className="font-medium text-[#7C2D12]">
          {issue.label}
          <span className="ml-2 text-xs font-normal text-[#92400E]">checked from {origin}</span>
        </div>
        <div className="text-xs text-[#78350F] mt-0.5">{issue.hint}</div>
        <div className="text-[11px] text-[#92400E] mt-1 font-mono break-all">
          Endpoint: <span className="select-all">{API}/health</span>
        </div>
      </div>
      <button onClick={ping} disabled={checking}
              data-testid="admin-health-retry"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white border border-[#F59E0B]/40 rounded-sm hover:bg-[#FEF3C7] disabled:opacity-60">
        <RefreshCw size={12} className={checking ? "animate-spin" : ""}/> Retry
      </button>
      <button onClick={() => setDismissed(true)}
              data-testid="admin-health-dismiss"
              className="inline-flex items-center text-xs px-2 py-1.5 text-[#92400E] hover:text-[#7C2D12]"
              aria-label="Dismiss">
        <X size={14}/>
      </button>
    </div>
  );
}

/**
 * Small status pill used inside the sidebar — green when healthy, amber on warn.
 * Keeps a quiet visible signal even when the banner is dismissed.
 */
export function AdminHealthPill() {
  const [ok, setOk] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.get("/health", { timeout: 8000 });
        if (!cancelled) setOk(r.data?.db !== false);
      } catch { if (!cancelled) setOk(false); }
    };
    check();
    const t = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (ok === null) return null;
  return (
    <div data-testid="admin-health-pill"
         className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${
           ok ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-amber-300 bg-amber-50 text-amber-800"
         }`}>
      {ok
        ? <><CheckCircle2 size={10}/> API ok</>
        : <><AlertTriangle size={10}/> API down</>}
    </div>
  );
}
